import { defineStore } from 'pinia'
import { ref } from 'vue'
import carteService, { type Carte } from '@/services/carteService'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const useCarteStore = defineStore('carte', () => {
  const cartes = ref<Carte[]>([])
  const loading = ref(false)
  let fetchedAt = 0
  let pendingPromise: Promise<Carte[]> | null = null

  function isFresh(): boolean {
    return fetchedAt > 0 && Date.now() - fetchedAt < CACHE_TTL
  }

  async function fetchCartes(force = false): Promise<Carte[]> {
    if (!force && isFresh() && cartes.value.length > 0) {
      return cartes.value
    }
    if (pendingPromise) return pendingPromise

    pendingPromise = (async () => {
      loading.value = true
      try {
        const res = await carteService.getCartes()
        if (res.data) {
          cartes.value = Array.isArray(res.data) ? res.data : []
          fetchedAt = Date.now()
        }
        return cartes.value
      } finally {
        loading.value = false
        pendingPromise = null
      }
    })()

    return pendingPromise
  }

  function invalidate() {
    fetchedAt = 0
  }

  function $reset() {
    cartes.value = []
    fetchedAt = 0
    pendingPromise = null
    loading.value = false
  }

  return {
    cartes,
    loading,
    fetchCartes,
    invalidate,
    $reset,
  }
})
